import { Dispatch } from 'react-redux';
import { Action } from '../../../actions';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';
import { StoreState } from '../../../reducers';
import { receiveResolutions, receiveDriveItems } from '../actions';
import UserAgentUtils from '../../../utils/userAgent';
import { downloadThumb } from '../../OfflineManager/actions';
import { DownloadPriority } from '../../../utils/nativeApi';
import IndexedDBManager from '../../../utils/indexedDB/manager';

enum NewDriveItemsActionKeys {
	REQUEST_NEW_DRIVE_ITEMS = '@@newDriveItem/REQUEST_NEW_DRIVE_ITEMS',
	RECEIVE_NEW_DRIVE_ITEMS = '@@newDriveItem/RECEIVE_NEW_DRIVE_ITEMS',
	UPDATE_NEW_ITEMS = '@@newDriveItem/UPDATE_NEW_ITEMS',
}

interface StoredNewItemInfo {
	id: number;
	additionDate: number;
}

interface RequestNewDriveItemsAction extends Action {
	type: NewDriveItemsActionKeys.REQUEST_NEW_DRIVE_ITEMS;
}

interface ReceiveNewDriveItemsAction extends Action {
	type: NewDriveItemsActionKeys.RECEIVE_NEW_DRIVE_ITEMS;
}

interface UpdateNewItemsAction extends Action {
	type: NewDriveItemsActionKeys.UPDATE_NEW_ITEMS;
	ids: number[];
}

type NewDriveItemsAction = RequestNewDriveItemsAction
	| ReceiveNewDriveItemsAction
	| UpdateNewItemsAction;

const loadNewItemsStore = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const storedNewItems = await IndexedDBManager.getNewItems();

	if (storedNewItems) {
		dispatch({
			type: NewDriveItemsActionKeys.UPDATE_NEW_ITEMS,
			ids: storedNewItems.map(({ id }) => id),
		});
	}
};

const requestNewDriveItems = (): RequestNewDriveItemsAction => ({
	type: NewDriveItemsActionKeys.REQUEST_NEW_DRIVE_ITEMS,
});

const receiveNewDriveItems = (): ReceiveNewDriveItemsAction => ({
	type: NewDriveItemsActionKeys.RECEIVE_NEW_DRIVE_ITEMS,
});

const updateNewItems = (metas: { [k: number]: Api.DriveItem }, ordered: number[]) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const isFolder = (id: number) => metas[id].type === Api.DriveItemType.Directory;
	const filteredIds = ordered.filter(id => !isFolder(id));

	dispatch({
		type: NewDriveItemsActionKeys.UPDATE_NEW_ITEMS,
		ids: filteredIds,
	});

	const newItemsToStore: StoredNewItemInfo[] = filteredIds.map(id => ({
		id,
		additionDate: Date.parse(metas[id].creationDate),
	}));

	IndexedDBManager.setNewItems(newItemsToStore).then();

	const { driveList: { driveItems } } = getState();
	filteredIds.forEach((id) => {
		if (UserAgentUtils.isMobileApp && !driveItems[id]) {
			dispatch(downloadThumb(id, DownloadPriority.Normal, false, metas[id]));
		}
	});
};

const fetchNewDriveItems = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		dispatch(requestNewDriveItems());

		if (UserAgentUtils.isOnline()) {
			const { metas, resolutions, ordered } = await ApiUtils.getNewContents();
			dispatch(updateNewItems(metas, ordered));

			if (resolutions) {
				dispatch(receiveResolutions(resolutions));
			}

			dispatch(receiveDriveItems(metas));
		} else {
			dispatch(updateNewItemsStoreOffline());
		}

		dispatch(receiveNewDriveItems());
	} catch (error) {
		dispatch(receiveNewDriveItems());
	}
};

const updateNewItemsStoreOffline = () => async (dispatch: Dispatch<StoreState>) => {
	const storedNewItems = await IndexedDBManager.getNewItems();

	if (!storedNewItems) {
		return;
	}

	const MAX_LAST_VISIT_DAYS = 30;
	const MAX_VISIT_DATE_MS = Date.now() - (1000 * 3600 * 24 * MAX_LAST_VISIT_DAYS);
	const filteredNewItems = storedNewItems.filter(({ additionDate }) => additionDate >= MAX_VISIT_DATE_MS);

	dispatch({
		type: NewDriveItemsActionKeys.UPDATE_NEW_ITEMS,
		ids: filteredNewItems.map(({ id }) => id),
	});

	IndexedDBManager.setNewItems(filteredNewItems).then();
};

export {
	NewDriveItemsActionKeys,
	NewDriveItemsAction,
	fetchNewDriveItems,
	StoredNewItemInfo,
	loadNewItemsStore,
	updateNewItemsStoreOffline,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/New/actions.ts